import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listForms,
  createForm,
  deleteForm,
  listItems,
  createItem,
  deleteItem,
  listPending,
  submitEvaluation,
  listEvaluations,
  agentSummary,
  type EvalItem,
  type PendingCall,
} from "../api/evaluation";

type Tab = "forms" | "evaluate" | "reports";

export function EvaluationPage() {
  const [tab, setTab] = useState<Tab>("evaluate");

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Quality Evaluation</h1>
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {(["evaluate", "forms", "reports"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize -mb-px border-b-2 ${
              tab === t ? "border-brand text-brand" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "forms" && <FormsTab />}
      {tab === "evaluate" && <EvaluateTab />}
      {tab === "reports" && <ReportsTab />}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Forms + items management
// --------------------------------------------------------------------------- //
function FormsTab() {
  const qc = useQueryClient();
  const [selectedForm, setSelectedForm] = useState<number | null>(null);
  const [newFormName, setNewFormName] = useState("");
  const [newFormDesc, setNewFormDesc] = useState("");

  const { data: forms, isLoading } = useQuery({ queryKey: ["eval-forms"], queryFn: listForms });

  const addForm = useMutation({
    mutationFn: () => createForm({ name: newFormName, description: newFormDesc }),
    onSuccess: () => {
      setNewFormName("");
      setNewFormDesc("");
      qc.invalidateQueries({ queryKey: ["eval-forms"] });
    },
  });

  const removeForm = useMutation({
    mutationFn: (id: number) => deleteForm(id),
    onSuccess: () => {
      setSelectedForm(null);
      qc.invalidateQueries({ queryKey: ["eval-forms"] });
    },
  });

  if (isLoading) return <p className="text-gray-500">Loading forms…</p>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div>
        <div className="bg-white rounded-lg shadow overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Scorecard</th>
                <th className="px-4 py-2">Items</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(forms ?? []).map((f) => (
                <tr
                  key={f.evaluation_form_id}
                  className={`cursor-pointer ${selectedForm === f.evaluation_form_id ? "bg-brand/5" : "hover:bg-gray-50"}`}
                  onClick={() => setSelectedForm(f.evaluation_form_id)}
                >
                  <td className="px-4 py-2">
                    <div className="font-medium">{f.name}</div>
                    <div className="text-gray-500 text-xs">{f.description || "—"}</div>
                  </td>
                  <td className="px-4 py-2">{f.item_count}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Delete scorecard "${f.name}"?`)) removeForm.mutate(f.evaluation_form_id);
                      }}
                      className="text-red-600 hover:underline text-xs"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-lg shadow p-4 space-y-2">
          <div className="text-sm font-semibold text-gray-700">New scorecard</div>
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="Name"
            value={newFormName}
            onChange={(e) => setNewFormName(e.target.value)}
          />
          <input
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            placeholder="Description"
            value={newFormDesc}
            onChange={(e) => setNewFormDesc(e.target.value)}
          />
          <button
            disabled={!newFormName || addForm.isPending}
            onClick={() => addForm.mutate()}
            className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
          >
            Add scorecard
          </button>
        </div>
      </div>
      <div>{selectedForm !== null ? <ItemsEditor formId={selectedForm} /> : <p className="text-gray-400 text-sm">Select a scorecard to edit its items.</p>}</div>
    </div>
  );
}

function ItemsEditor({ formId }: { formId: number }) {
  const qc = useQueryClient();
  const { data: items } = useQuery({ queryKey: ["eval-items", formId], queryFn: () => listItems(formId) });
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [maxScore, setMaxScore] = useState(10);

  const add = useMutation({
    mutationFn: () =>
      createItem({ evaluation_form_id: formId, name, description: desc, max_score: maxScore, weight: 0, section: "behavior" }),
    onSuccess: () => {
      setName("");
      setDesc("");
      setMaxScore(10);
      qc.invalidateQueries({ queryKey: ["eval-items", formId] });
      qc.invalidateQueries({ queryKey: ["eval-forms"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteItem(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["eval-items", formId] });
      qc.invalidateQueries({ queryKey: ["eval-forms"] });
    },
  });

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="text-sm font-semibold text-gray-700 mb-3">Scored items</div>
      <div className="space-y-2 mb-4">
        {(items ?? []).map((it) => (
          <div key={it.evaluation_item_id} className="flex items-center justify-between border border-gray-100 rounded px-3 py-2">
            <div>
              <div className="text-sm font-medium">{it.name}</div>
              <div className="text-xs text-gray-500">{it.description || "—"}</div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">max {it.max_score}</span>
              <button onClick={() => remove.mutate(it.evaluation_item_id)} className="text-red-600 hover:underline text-xs">
                Remove
              </button>
            </div>
          </div>
        ))}
        {(items ?? []).length === 0 && <p className="text-gray-400 text-sm">No items yet.</p>}
      </div>
      <div className="space-y-2 border-t border-gray-100 pt-3">
        <input
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          placeholder="Item name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          placeholder="Description"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm text-gray-600">
          Max score
          <input
            type="number"
            min={0}
            max={100}
            className="w-20 border border-gray-300 rounded px-2 py-1"
            value={maxScore}
            onChange={(e) => setMaxScore(Number(e.target.value))}
          />
        </label>
        <button
          disabled={!name || add.isPending}
          onClick={() => add.mutate()}
          className="bg-brand hover:bg-brand-dark text-white rounded px-4 py-2 text-sm disabled:opacity-50"
        >
          Add item
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Evaluate a pending call
// --------------------------------------------------------------------------- //
function EvaluateTab() {
  const qc = useQueryClient();
  const { data: pending, isLoading } = useQuery({ queryKey: ["eval-pending"], queryFn: listPending });
  const { data: forms } = useQuery({ queryKey: ["eval-forms"], queryFn: listForms });
  const [active, setActive] = useState<PendingCall | null>(null);

  if (isLoading) return <p className="text-gray-500">Loading calls…</p>;

  if (active) {
    return (
      <Scorecard
        call={active}
        forms={(forms ?? []).map((f) => ({ id: f.evaluation_form_id, name: f.name }))}
        onDone={() => {
          setActive(null);
          qc.invalidateQueries({ queryKey: ["eval-pending"] });
          qc.invalidateQueries({ queryKey: ["eval-list"] });
        }}
        onCancel={() => setActive(null)}
      />
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-gray-500">
          <tr>
            <th className="px-4 py-2">When</th>
            <th className="px-4 py-2">Phone</th>
            <th className="px-4 py-2">Disposition</th>
            <th className="px-4 py-2">Talk (s)</th>
            <th className="px-4 py-2">Ext</th>
            <th className="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {(pending ?? []).map((c) => (
            <tr key={c.spool_id} className="hover:bg-gray-50">
              <td className="px-4 py-2 text-gray-500">{c.created_at || "—"}</td>
              <td className="px-4 py-2 font-mono">{c.phone}</td>
              <td className="px-4 py-2">{c.disposition || "—"}</td>
              <td className="px-4 py-2">{c.billsec ?? "—"}</td>
              <td className="px-4 py-2">{c.extension_id ?? "—"}</td>
              <td className="px-4 py-2 text-right">
                <button onClick={() => setActive(c)} className="text-brand hover:underline">
                  Evaluate
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {(pending ?? []).length === 0 && <p className="text-gray-400 text-sm p-4">No calls awaiting evaluation.</p>}
    </div>
  );
}

function Scorecard({
  call,
  forms,
  onDone,
  onCancel,
}: {
  call: PendingCall;
  forms: { id: number; name: string }[];
  onDone: () => void;
  onCancel: () => void;
}) {
  const [formId, setFormId] = useState<number | null>(forms[0]?.id ?? null);
  const { data: items } = useQuery({
    queryKey: ["eval-items", formId],
    queryFn: () => listItems(formId!),
    enabled: formId !== null,
  });
  const [scores, setScores] = useState<Record<number, number>>({});
  const [comments, setComments] = useState<Record<number, string>>({});
  const [comment, setComment] = useState("");

  const submit = useMutation({
    mutationFn: () =>
      submitEvaluation({
        spool_id: call.spool_id,
        evaluation_form_id: formId!,
        evaluator_comment: comment,
        scores: (items ?? []).map((it: EvalItem) => ({
          evaluation_item_id: it.evaluation_item_id,
          score: scores[it.evaluation_item_id] ?? 0,
          comment: comments[it.evaluation_item_id] ?? "",
        })),
      }),
    onSuccess: onDone,
  });

  const obtained = (items ?? []).reduce((sum, it) => sum + (scores[it.evaluation_item_id] ?? 0), 0);
  const maxTotal = (items ?? []).reduce((sum, it) => sum + it.max_score, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white rounded-lg shadow p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-lg font-bold font-mono">{call.phone}</div>
            <div className="text-xs text-gray-500">
              {call.disposition || "—"} · {call.billsec ?? 0}s · {call.created_at}
            </div>
          </div>
          <select
            className="border border-gray-300 rounded px-3 py-2 text-sm"
            value={formId ?? ""}
            onChange={(e) => setFormId(Number(e.target.value))}
          >
            {forms.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-3">
          {(items ?? []).map((it) => (
            <div key={it.evaluation_item_id} className="border border-gray-100 rounded p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">{it.name}</div>
                  <div className="text-xs text-gray-500">{it.description || "—"}</div>
                </div>
                <select
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                  value={scores[it.evaluation_item_id] ?? 0}
                  onChange={(e) =>
                    setScores((s) => ({ ...s, [it.evaluation_item_id]: Number(e.target.value) }))
                  }
                >
                  {Array.from({ length: it.max_score + 1 }, (_, n) => (
                    <option key={n} value={n}>
                      {n} / {it.max_score}
                    </option>
                  ))}
                </select>
              </div>
              <input
                className="w-full border border-gray-200 rounded px-2 py-1 text-xs mt-2"
                placeholder="Comment (optional)"
                value={comments[it.evaluation_item_id] ?? ""}
                onChange={(e) =>
                  setComments((c) => ({ ...c, [it.evaluation_item_id]: e.target.value }))
                }
              />
            </div>
          ))}
          {(items ?? []).length === 0 && (
            <p className="text-gray-400 text-sm">This scorecard has no items. Add some on the Forms tab.</p>
          )}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-5 h-fit space-y-3">
        <div className="text-center">
          <div className="text-4xl font-bold text-brand">
            {maxTotal > 0 ? Math.round((100 * obtained) / maxTotal) : 0}%
          </div>
          <div className="text-xs text-gray-500">
            {obtained} / {maxTotal} points
          </div>
        </div>
        <textarea
          className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          rows={4}
          placeholder="Overall evaluator comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <button
          disabled={!formId || submit.isPending || (items ?? []).length === 0}
          onClick={() => submit.mutate()}
          className="w-full bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
        >
          {submit.isPending ? "Saving…" : "Submit evaluation"}
        </button>
        <button onClick={onCancel} className="w-full bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Reports
// --------------------------------------------------------------------------- //
function ReportsTab() {
  const [view, setView] = useState<"calls" | "agents">("calls");
  const { data: calls } = useQuery({ queryKey: ["eval-list"], queryFn: () => listEvaluations() });
  const { data: agents } = useQuery({ queryKey: ["eval-agents"], queryFn: () => agentSummary() });

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {(["calls", "agents"] as const).map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`px-3 py-1 text-sm rounded capitalize ${
              view === v ? "bg-brand text-white" : "bg-gray-100 text-gray-600"
            }`}
          >
            {v === "calls" ? "Per call" : "Per agent"}
          </button>
        ))}
      </div>
      {view === "calls" ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Phone</th>
                <th className="px-4 py-2">Scorecard</th>
                <th className="px-4 py-2">Ext</th>
                <th className="px-4 py-2 text-right">Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(calls ?? []).map((e) => (
                <tr key={e.evaluation_id}>
                  <td className="px-4 py-2 text-gray-500">{e.created_at || "—"}</td>
                  <td className="px-4 py-2 font-mono">{e.phone || "—"}</td>
                  <td className="px-4 py-2">{e.form_name}</td>
                  <td className="px-4 py-2">{e.extension_id ?? "—"}</td>
                  <td className="px-4 py-2 text-right font-semibold">{e.percent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(calls ?? []).length === 0 && <p className="text-gray-400 text-sm p-4">No evaluations yet.</p>}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Extension</th>
                <th className="px-4 py-2 text-right">Calls evaluated</th>
                <th className="px-4 py-2 text-right">Avg score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(agents ?? []).map((a, i) => (
                <tr key={a.extension_id ?? `none-${i}`}>
                  <td className="px-4 py-2">{a.extension_id ?? "Unassigned"}</td>
                  <td className="px-4 py-2 text-right">{a.calls_evaluated}</td>
                  <td className="px-4 py-2 text-right font-semibold">{a.avg_percent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {(agents ?? []).length === 0 && <p className="text-gray-400 text-sm p-4">No agent data yet.</p>}
        </div>
      )}
    </div>
  );
}
